import React from 'react';
import { useAppContext, useClock } from '../context/AppContext';
import { classNames, formatCedi, formatDateTime, relativeTimeFrom, calcItemsTotal, sanitizeText, sanitizeNumeric } from '../utils/formatters';
import { methodLabels, getDiscountReasonLabel } from '../utils/constants';
import {
  getOrderDeliveryRevenue,
  getOrderDiscountAmount,
  getOrderItemRevenue,
  getOrderPackagingRevenue,
} from '../utils/analytics';
import StatusPill from './StatusPill';
import OrderTypePill from './OrderTypePill';
import AmendmentHistory from './AmendmentHistory';
import ExpenseCategorySelect from './ExpenseCategorySelect';
import EodReport, { printEodReport } from './EodReport';
import { canViewFinancialReports } from '../utils/permissions';
import { Modal, Button, ConfirmDialog } from './ui';

const CATEGORY_LABELS = {
  mainPackages: 'Main Packages',
  promotions: 'Promotions',
  extras: 'Extras',
};
const CATEGORY_ORDER = ['mainPackages', 'promotions', 'extras'];

const buildCategoryLookup = (menuData) => {
  const map = new Map();
  if (!menuData) return map;
  CATEGORY_ORDER.forEach((cat) => {
    (menuData[cat] || []).forEach((entry) => {
      if (entry?.name) map.set(entry.name, cat);
    });
  });
  return map;
};

const groupItemsByCategory = (items = [], categoryLookup) => {
  const groups = new Map();
  items.forEach((item) => {
    const key = item.category || categoryLookup?.get(item.name) || 'other';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });
  const ordered = [];
  CATEGORY_ORDER.forEach((key) => {
    if (groups.has(key)) {
      ordered.push({ key, label: CATEGORY_LABELS[key], items: groups.get(key) });
      groups.delete(key);
    }
  });
  groups.forEach((groupItems, key) => {
    ordered.push({ key, label: CATEGORY_LABELS[key] || 'Other', items: groupItems });
  });
  return ordered;
};

const renderItemRow = (item, idx) => (
  <div key={idx} className="flex justify-between items-center">
    <div>
      <p className="font-medium">{item.name}</p>
      <p className="text-sm text-gray-600">{item.quantity} × {formatCedi(item.price)}</p>
    </div>
    <p className="font-bold text-green-600">{formatCedi(item.quantity * item.price)}</p>
  </div>
);

const ItemGroups = ({ items, categoryLookup }) => {
  const groups = groupItemsByCategory(items, categoryLookup);
  if (groups.length <= 1) {
    return <div className="space-y-2">{items.map(renderItemRow)}</div>;
  }
  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <div key={group.key}>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{group.label}</div>
          <div className="space-y-2">{group.items.map(renderItemRow)}</div>
        </div>
      ))}
    </div>
  );
};

const AppModals = () => {
  const ctx = useAppContext();
  const { kitchenNow } = useClock();
  const {
    // Order Details
    showOrderDetails, setShowOrderDetails, selectedOrder,
    printReceiptWindow, openRiderAssignmentModal, getOrderAssignedRider, getOrderDeliveryFee, getOrderDeliveryVerificationCode,
    getOrderDiscountAdjustment, getPendingDiscountRequest,
    // Kitchen Display
    kitchenDisplayMode, setKitchenDisplayMode, getOrdersFor, updateOrderStatus, showToast,
    // Inventory Modal
    showInventoryModal, closeInventoryModal, editInventoryItem, inventoryForm, setInventoryForm, handleInventorySave,
    // Amend Modal
    showAmendModal, amendTarget, closeAmend, amendModalTab, setAmendModalTab,
    amendDraftBundles, setAmendDraftBundles, amendAdd, amendUpdateQty, amendRemove, amendAddBundle,
    amendPaymentMethod, setAmendPaymentMethod, amendSplitPayments, setAmendSplitPayments,
    amendCustomerName, setAmendCustomerName, amendCustomerPhone, setAmendCustomerPhone,
    amendCustomerAddress, setAmendCustomerAddress, amendReason, setAmendReason,
    confirmAmend, currentUser, menuData,
    // Expense Edit
    showExpenseEditModal, editExpenseTarget, closeEditExpense, expenseDraft, setExpenseDraft,
    confirmEditExpense,
    // Expense Confirm Dialog
    confirmDialog, setConfirmDialog, confirmExpenseAction,
    // Expense Delete Confirm
    showExpenseDeleteConfirm, setShowExpenseDeleteConfirm, setDeleteExpenseId, confirmDeleteExpense,
    // Export Modal
    showExportModal, setShowExportModal, exportRangeType, setExportRangeType,
    exportSingleDate, setExportSingleDate, exportStartDate, setExportStartDate,
    exportEndDate, setExportEndDate, exportTypes, setExportTypes,
    exportOrderStatuses, setExportOrderStatuses, runExport, orders, expenses,
    // Split Payment Modal
    showSplitPaymentModal, setShowSplitPaymentModal, splitPayments, setSplitPayments,
    grandTotal, setPosPaymentMethod,
    // Rider Assignment Modal
    showRiderAssignmentModal, riderAssignmentTarget, riderAssignmentForm, setRiderAssignmentForm,
    closeRiderAssignmentModal, confirmRiderAssignment, riders, riderPresence,
    // Order Confirm
    showOrderConfirm, setShowOrderConfirm, confirmPlaceOrder,
    // Celebrate
    celebrateOrder,
    // Toast
    notification,
    // EOD Report
    showEodReport, setShowEodReport, eodReportDate, setEodReportDate, computeEodReport,
  } = ctx;
  const noop = () => {};
  const riderPresenceById = new Map((riderPresence || []).map((entry) => [entry.rider_id, entry]));
  const riderDeliveryTarget = riderAssignmentTarget;
  const riderDeliveryForm = { riderName: '', riderPhone: '', payout: '', note: '' };
  const setRiderDeliveryForm = noop;
  const closeRiderDeliveryModal = closeRiderAssignmentModal;
  const confirmRiderDelivery = confirmRiderAssignment;
  const selectedRiderStatus = riderAssignmentForm?.riderId
    ? (riderPresenceById.get(riderAssignmentForm.riderId)?.availability_status || 'offline')
    : '';
  const canAssignSelectedRider = ['online', 'delivering'].includes(selectedRiderStatus);
  const categoryLookup = React.useMemo(() => buildCategoryLookup(menuData), [menuData]);

  return (
    <>
      {/* Order Details Modal */}
      <Modal
        isOpen={Boolean(showOrderDetails && selectedOrder)}
        onClose={() => setShowOrderDetails(false)}
        title="Order Details"
        size="lg"
        footer={selectedOrder && (
          <>
            <Button variant="ghost" onClick={() => printReceiptWindow(selectedOrder)}>
              🖨️ Print Receipt
            </Button>
            <Button variant="secondary" onClick={() => setShowOrderDetails(false)}>
              Close
            </Button>
          </>
        )}
      >
        {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="text-sm text-gray-600">Order ID:</span><p className="font-medium">#{selectedOrder.id.slice(-6)}</p></div>
                <div><span className="text-sm text-gray-600">Placed:</span><p className="font-medium">{formatDateTime(selectedOrder.timestamp)}</p></div>
                <div><span className="text-sm text-gray-600">Type:</span><div className="mt-1"><OrderTypePill orderType={selectedOrder.orderType}/></div></div>
                <div><span className="text-sm text-gray-600">Processed by:</span><p className="font-medium">{selectedOrder.processedBy}</p></div>
                <div><span className="text-sm text-gray-600">Status:</span><div className="mt-1"><StatusPill status={selectedOrder.status}/></div></div>
                {selectedOrder.paymentMethod && (
                  <div>
                    <span className="text-sm text-gray-600">Payment Method:</span>
                    <p className="font-medium">{methodLabels[selectedOrder.paymentMethod] || selectedOrder.paymentMethod}</p>
                    {selectedOrder.paymentMethod === 'split' && selectedOrder.splitPayments && (
                      <div className="mt-2 space-y-1">
                        {selectedOrder.splitPayments.map((payment, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs bg-gray-50 px-2 py-1 rounded">
                            <span>
                              {payment.method === 'momo' ? '📱' : payment.method === 'cash' ? '💵' : '💳'}
                              {' '}{methodLabels[payment.method]}
                            </span>
                            <span className="font-semibold">{formatCedi(payment.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {selectedOrder.orderType === 'online' && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Customer Information</h4>
                  {(selectedOrder.customerName || selectedOrder.customerPhone || selectedOrder.deliveryAddress || selectedOrder.notes) ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {selectedOrder.customerName && (<div><span className="text-sm text-blue-600">Name:</span><p className="font-medium">{selectedOrder.customerName}</p></div>)}
                      {selectedOrder.customerPhone && (<div><span className="text-sm text-blue-600">Phone:</span><p className="font-medium">{selectedOrder.customerPhone}</p></div>)}
                      {selectedOrder.deliveryAddress && (<div className="md:col-span-2"><span className="text-sm text-blue-600">Delivery Address:</span><p className="font-medium">{selectedOrder.deliveryAddress}</p></div>)}
                      {selectedOrder.notes && (<div className="md:col-span-2"><span className="text-sm text-blue-600">Notes:</span><p className="font-medium">{selectedOrder.notes}</p></div>)}
                    </div>
                  ) : (<p className="text-blue-600 text-sm italic">No customer information available.</p>)}
                </div>
              )}
              {selectedOrder.orderType === 'online' && (() => {
                const riderInfo = getOrderAssignedRider(selectedOrder);
                const deliveryFee = getOrderDeliveryFee(selectedOrder);
                const deliveryVerificationCode = getOrderDeliveryVerificationCode(selectedOrder);
                return (
                  <div className="bg-sky-50 rounded-lg p-4">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <h4 className="font-semibold text-sky-800">Rider Assignment</h4>
                      {(selectedOrder.status === 'paid' || selectedOrder.status === 'ready' || selectedOrder.status === 'delivered') && (
                        <button
                          onClick={() => openRiderAssignmentModal(selectedOrder)}
                          className="px-3 py-1.5 bg-[#B42318] text-white hover:bg-[#912018] rounded-lg text-xs font-semibold transition-colors"
                        >
                          {riderInfo ? 'Update rider' : 'Assign rider'}
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <span className="text-sm text-sky-600">Rider:</span>
                        <p className="font-medium">{riderInfo?.riderName || 'Not assigned yet'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-sky-600">Payout:</span>
                        <p className="font-medium">{formatCedi(deliveryFee)}</p>
                      </div>
                      {deliveryVerificationCode && (
                        <div>
                          <span className="text-sm text-sky-600">Delivery code:</span>
                          <p className="font-mono text-lg font-semibold tracking-[0.24em]">{deliveryVerificationCode}</p>
                        </div>
                      )}
                      {riderInfo?.riderPhone && (
                        <div>
                          <span className="text-sm text-sky-600">Phone:</span>
                          <p className="font-medium">{riderInfo.riderPhone}</p>
                        </div>
                      )}
                      {riderInfo?.status && (
                        <div>
                          <span className="text-sm text-sky-600">Assignment status:</span>
                          <p className="font-medium capitalize">{String(riderInfo.status).replace(/_/g, ' ')}</p>
                        </div>
                      )}
                      {riderInfo?.note && (
                        <div className="md:col-span-2">
                          <span className="text-sm text-sky-600">Note:</span>
                          <p className="font-medium">{riderInfo.note}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              {selectedOrder.orderType === 'in-person' && typeof selectedOrder.paymentAmount === 'number' && (
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">Payment Information</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div><span className="text-sm text-green-600">Amount Paid:</span><p className="font-medium">{formatCedi(selectedOrder.paymentAmount)}</p></div>
                    <div><span className="text-sm text-green-600">Change Given:</span><p className="font-medium">{formatCedi(Number(selectedOrder.balanceGiven) || 0)}</p></div>
                  </div>
                </div>
              )}
              {/* Timeline */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-3">Timeline</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-gray-400"></div>
                    <div><div className="text-sm font-medium text-gray-800">Placed</div><div className="text-xs text-gray-600">{formatDateTime(selectedOrder.timestamp)}</div></div>
                  </div>
                  {selectedOrder.paidAt && (<div className="flex items-start gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-blue-500"></div>
                    <div><div className="text-sm font-medium text-blue-800">Paid</div><div className="text-xs text-gray-600">{formatDateTime(selectedOrder.paidAt)} • {relativeTimeFrom(selectedOrder.paidAt)}</div></div>
                  </div>)}
                  {selectedOrder.readyAt && (<div className="flex items-start gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-amber-500"></div>
                    <div><div className="text-sm font-medium text-amber-800">Ready</div><div className="text-xs text-gray-600">{formatDateTime(selectedOrder.readyAt)} • {relativeTimeFrom(selectedOrder.readyAt)}</div></div>
                  </div>)}
                  {selectedOrder.completedAt && (<div className="flex items-start gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-green-600"></div>
                    <div><div className="text-sm font-medium text-green-800">{selectedOrder.status === 'picked_up' ? 'Picked Up' : 'Delivered'}</div>
                      <div className="text-xs text-gray-600">{formatDateTime(selectedOrder.completedAt)} • {relativeTimeFrom(selectedOrder.completedAt)}</div></div>
                  </div>)}
                </div>
              </div>
              <AmendmentHistory order={selectedOrder} />
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Items Ordered</h4>
                {selectedOrder.bundles ? (
                  <div className="space-y-4">
                    {selectedOrder.bundles.map((bundle, bIdx) => (
                      <div key={bIdx} className="bg-gray-50 rounded-lg p-4">
                        <h5 className="font-medium mb-2">{bundle.name}</h5>
                        <ItemGroups items={bundle.items} categoryLookup={categoryLookup} />
                        <div className="mt-2 text-right font-semibold">Subtotal: {formatCedi(calcItemsTotal(bundle.items))}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <ItemGroups items={selectedOrder.items} categoryLookup={categoryLookup} />
                  </div>
                )}
                {(() => {
                  const itemsSubtotal = getOrderItemRevenue(selectedOrder);
                  const packagingCharge = getOrderPackagingRevenue(selectedOrder);
                  const deliveryCharge = getOrderDeliveryRevenue(selectedOrder);
                  const discountValue = getOrderDiscountAmount(selectedOrder);
                  const discountAdjustment = getOrderDiscountAdjustment(selectedOrder);
                  const discountRequest = getPendingDiscountRequest(selectedOrder);

                  return (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-1.5">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Items subtotal</span>
                        <span>{formatCedi(itemsSubtotal)}</span>
                      </div>
                      {packagingCharge > 0 && (
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Packaging</span>
                          <span>{formatCedi(packagingCharge)}</span>
                        </div>
                      )}
                      {deliveryCharge > 0 && (
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Delivery fee</span>
                          <span>{formatCedi(deliveryCharge)}</span>
                        </div>
                      )}
                      {discountValue > 0 && (
                        <div className="flex justify-between text-sm font-medium text-emerald-600">
                          <span>
                            Discount{discountAdjustment?.percent ? ` (${discountAdjustment.percent}%)` : ''}
                            {discountAdjustment?.reason ? ` · ${getDiscountReasonLabel(discountAdjustment.reason, discountAdjustment.reasonDetail)}` : ''}
                          </span>
                          <span>−{formatCedi(discountValue)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                        <span className="text-lg font-semibold">Total:</span>
                        <span className="text-xl font-bold text-blue-600">{formatCedi(selectedOrder.total)}</span>
                      </div>
                      {discountAdjustment?.approvedBy && (
                        <div className="text-xs text-gray-500 pt-1">
                          Discount approved by {discountAdjustment.approvedBy}
                          {discountAdjustment.appliedBy && discountAdjustment.appliedBy !== discountAdjustment.approvedBy
                            ? ` (requested by ${discountAdjustment.appliedBy})`
                            : ''}
                        </div>
                      )}
                      {discountRequest && (
                        <div className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                          {discountRequest.payload?.percent}% discount requested by {discountRequest.requestedBy} —
                          awaiting admin approval. Payment is locked and the customer owes the full {formatCedi(selectedOrder.total)} until it is decided.
                        </div>
                      )}
                    </div>
                  );
                })()}
                {(selectedOrder.dueAmount > 0) && (
                  <div className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                    Difference to collect: {formatCedi(selectedOrder.dueAmount)} (order returned to Queue)
                  </div>
                )}
                {(selectedOrder.refundDue > 0) && (
                  <div className="mt-3 text-sm text-purple-700 bg-purple-50 border border-purple-200 rounded-md p-2">
                    Refund due to customer: {formatCedi(selectedOrder.refundDue)}
                  </div>
                )}
              </div>
            </div>
        )}
      </Modal>

      {/* Kitchen Display Mode */}
      {kitchenDisplayMode && (
        <div className="fixed inset-0 z-[100] bg-gray-950 overflow-auto">
          <div className="flex items-center justify-between px-6 py-4 bg-gray-900 border-b border-gray-700">
            <div className="flex items-center gap-4">
              <span className="text-3xl">👨‍🍳</span>
              <div>
                <h1 className="text-2xl font-bold text-white">KITCHEN DISPLAY</h1>
                <p className="text-green-400 text-sm font-medium">Komis Kenkey — Live Orders</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{getOrdersFor('paid').length}</div>
                <div className="text-xs text-gray-400 uppercase">Active</div>
              </div>
              <div className="text-right text-gray-300 text-sm">
                <div className="font-semibold">{new Date().toLocaleTimeString()}</div>
                <div className="text-xs text-gray-500">{new Date().toLocaleDateString()}</div>
              </div>
              <button
                onClick={() => setKitchenDisplayMode(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold text-sm transition-colors"
              >
                ✕ Exit
              </button>
            </div>
          </div>
          <div className="flex items-center gap-6 px-6 py-3 bg-gray-900/50 border-b border-gray-800 text-xs font-medium">
            <span className="flex items-center gap-2 text-green-400"><span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span> &lt; 10 min — On track</span>
            <span className="flex items-center gap-2 text-amber-400"><span className="w-3 h-3 rounded-full bg-amber-500 inline-block"></span> 10–15 min — Getting urgent</span>
            <span className="flex items-center gap-2 text-red-400"><span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span> &gt; 15 min — OVERDUE</span>
          </div>
          {getOrdersFor('paid').length === 0 ? (
            <div className="flex flex-col items-center justify-center h-3/4 text-gray-600">
              <div className="text-8xl mb-6">🍳</div>
              <div className="text-3xl font-bold">All caught up!</div>
              <div className="text-lg mt-2">No orders in the kitchen right now.</div>
            </div>
          ) : (
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {getOrdersFor('paid').map(order => {
                const paidMs = order.paidAt ? kitchenNow - new Date(order.paidAt).getTime() : 0;
                const prepMin = Math.floor(paidMs / 60000);
                const urgency = prepMin >= 15 ? 'red' : prepMin >= 10 ? 'amber' : 'green';
                const borderColor = urgency === 'red' ? 'border-red-500' : urgency === 'amber' ? 'border-amber-400' : 'border-green-500';
                const headerBg = urgency === 'red' ? 'bg-red-600' : urgency === 'amber' ? 'bg-amber-500' : 'bg-green-600';
                const timerText = urgency === 'red' ? 'text-red-300' : urgency === 'amber' ? 'text-amber-300' : 'text-green-300';
                return (
                  <div key={order.id} className={`bg-gray-900 rounded-2xl border-2 ${borderColor} overflow-hidden shadow-xl flex flex-col`}>
                    <div className={`${headerBg} px-4 py-3 flex items-center justify-between`}>
                      <div>
                        <div className="text-white font-bold text-lg">#{order.id.slice(-6)}</div>
                        <div className="text-white/80 text-xs">{order.customerName || (order.orderType === 'in-person' ? 'Walk-in' : 'Online')}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-black ${timerText} drop-shadow`}>{prepMin}m</div>
                        <div className="text-white/70 text-[10px]">
                          {urgency === 'red' ? '🔴 OVERDUE' : urgency === 'amber' ? '🟡 URGENT' : '🟢 OK'}
                        </div>
                      </div>
                    </div>
                    <div className="px-4 py-2 flex items-center gap-2 border-b border-gray-800">
                      <span className={classNames('px-2 py-0.5 text-[10px] rounded-full font-semibold',
                        order.orderType === 'in-person' ? 'bg-green-900 text-green-300' : 'bg-blue-900 text-blue-300'
                      )}>
                        {order.orderType === 'in-person' ? '🏃 Walk-in' : '🛵 Delivery'}
                      </span>
                      {order.notes && <span className="text-[10px] text-yellow-400 truncate">📝 {order.notes}</span>}
                    </div>
                    <div className="flex-1 px-4 py-3 overflow-y-auto custom-scrollbar space-y-1.5">
                      {(order.bundles || [{ name: null, items: order.items }]).map((bundle, bIdx) => (
                        <div key={bIdx}>
                          {bundle.name && <div className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-1">{bundle.name}</div>}
                          {bundle.items.map((item, iIdx) => (
                            <div key={iIdx} className="flex items-center justify-between text-sm">
                              <span className="text-gray-200 truncate pr-2">{item.name}</span>
                              <span className="text-white font-bold shrink-0 bg-gray-800 px-2 py-0.5 rounded text-xs">×{item.quantity}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                    <div className="px-4 py-3 border-t border-gray-800">
                      <button
                        onClick={() => { updateOrderStatus(order.id, 'ready'); showToast(`Order #${order.id.slice(-6)} marked READY`, 'success'); }}
                        className="w-full py-3 bg-green-500 hover:bg-green-400 text-white font-bold rounded-xl text-sm transition-colors button-hover shadow-md"
                      >
                        ✅ Mark Ready
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Inventory Add/Edit Modal */}
      {showInventoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <span>📦</span> {editInventoryItem ? 'Edit' : 'Add'} Inventory Item
              </h3>
              <button onClick={closeInventoryModal} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Item Name *</label>
                <input type="text" value={inventoryForm.itemName} onChange={(e) => setInventoryForm({ ...inventoryForm, itemName: sanitizeText(e.target.value, 100) })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="e.g., Kenkey, Chicken, Fish" maxLength={100} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Current Stock *</label>
                <input type="number" min="0" value={inventoryForm.currentStock} onChange={(e) => setInventoryForm({ ...inventoryForm, currentStock: sanitizeNumeric(e.target.value, { min: 0, max: 99999 }) })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="e.g., 100" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Low Stock Alert Threshold *</label>
                <input type="number" min="0" value={inventoryForm.lowStockThreshold} onChange={(e) => setInventoryForm({ ...inventoryForm, lowStockThreshold: sanitizeNumeric(e.target.value, { min: 0, max: 99999 }) })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="e.g., 10" />
                <p className="text-xs text-gray-500 mt-1">Alert will show when stock reaches this level</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Daily Default Quantity</label>
                <input type="number" min="0" value={inventoryForm.dailyDefault ?? ''} onChange={(e) => setInventoryForm({ ...inventoryForm, dailyDefault: sanitizeNumeric(e.target.value, { min: 0, max: 99999 }) })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="e.g., 40" />
                <p className="text-xs text-gray-500 mt-1">Quantity automatically restocked every day at 11pm</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Unit</label>
                <select value={inventoryForm.unit} onChange={(e) => setInventoryForm({ ...inventoryForm, unit: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500">
                  <option value="piece">Piece</option>
                  <option value="portion">Portion</option>
                  <option value="kg">Kilogram (kg)</option>
                  <option value="g">Gram (g)</option>
                  <option value="liter">Liter</option>
                  <option value="ml">Milliliter (ml)</option>
                  <option value="pack">Pack</option>
                  <option value="box">Box</option>
                  <option value="tin">Tin</option>
                  <option value="bottle">Bottle</option>
                  <option value="plate">Plate</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Cost Per Unit (GHS)</label>
                <input type="number" min="0" step="0.01" value={inventoryForm.costPerUnit} onChange={(e) => setInventoryForm({ ...inventoryForm, costPerUnit: sanitizeNumeric(e.target.value, { min: 0, max: 99999 }) })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="e.g., 5.00" />
                <p className="text-xs text-gray-500 mt-1">Used for calculating stock value and profit margins</p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={closeInventoryModal} className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 button-hover font-semibold">Cancel</button>
              <button onClick={handleInventorySave} className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 button-hover font-semibold">
                {editInventoryItem ? 'Update' : 'Add'} Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Amend Modal */}
      {showAmendModal && amendTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Amendment Order #{amendTarget.id.slice(-6)}</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    <StatusPill status={amendTarget.status} /> • {amendTarget.customerName || 'Guest'}
                  </p>
                </div>
                <button onClick={closeAmend} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">✕</button>
              </div>
            </div>
            <div className="px-6 border-b border-gray-200 bg-white">
              <div className="flex gap-6">
                <button onClick={() => setAmendModalTab('items')}
                  className={classNames('py-3 px-4 text-sm font-medium border-b-2 transition-colors',
                    amendModalTab === 'items' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
                  📦 Items & Packages
                </button>
                <button onClick={() => setAmendModalTab('details')}
                  className={classNames('py-3 px-4 text-sm font-medium border-b-2 transition-colors',
                    amendModalTab === 'details' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
                  📋 Order Details
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {amendModalTab === 'items' && (
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h4 className="text-lg font-semibold text-gray-800">📦 Packages</h4>
                        <button onClick={amendAddBundle} className="px-3 py-1.5 bg-[#B42318] text-white rounded-lg hover:bg-[#912018] text-sm button-hover flex items-center gap-1">
                          <span>+</span> Add Package
                        </button>
                      </div>
                      <div className="space-y-3">
                        {amendDraftBundles.map((bundle) => {
                          const bundleItems = Array.isArray(bundle.items) ? bundle.items : Object.values(bundle.items || {});
                          return (
                          <div key={bundle.id} className="bg-white rounded-xl p-4 border-2 border-gray-200 hover:border-blue-300 transition-colors">
                            <input type="text" value={bundle.name}
                              onChange={(e) => setAmendDraftBundles(prev => prev.map(b => b.id === bundle.id ? { ...b, name: e.target.value } : b))}
                              className="w-full font-semibold text-gray-800 mb-3 bg-transparent border-b border-gray-300 pb-2 focus:outline-none focus:border-blue-500" placeholder="Package name" />
                            {bundleItems.length > 0 ? (
                              <div className="space-y-2">
                                {bundleItems.map(item => (
                                  <div key={item.name} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-800 truncate">{item.name}</div>
                                      <div className="text-xs text-gray-500">{formatCedi(item.price)} each</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button onClick={() => amendUpdateQty(bundle.id, item.name, item.quantity - 1)} className="w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg button-hover flex items-center justify-center text-lg">−</button>
                                      <span className="w-12 text-center font-bold text-gray-800">{item.quantity}</span>
                                      <button onClick={() => amendUpdateQty(bundle.id, item.name, item.quantity + 1)} className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white rounded-lg button-hover flex items-center justify-center text-lg">+</button>
                                      <button onClick={() => amendRemove(bundle.id, item.name)} className="ml-2 text-red-500 hover:text-red-700 text-sm font-medium">🗑️</button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-4 text-sm text-gray-500 bg-gray-50 rounded-lg">No items yet. Add items from the menu →</div>
                            )}
                            {bundleItems.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-600">Package Total</span>
                                <span className="text-lg font-bold text-gray-800">{formatCedi(calcItemsTotal(bundleItems))}</span>
                              </div>
                            )}
                          </div>
                        )})}
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-800">🍽️ Menu</h4>
                      <p className="text-sm text-gray-600">Click items to add to the last package</p>
                      <div className="space-y-4">
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                          <div className="text-sm font-bold text-amber-900 mb-3 flex items-center gap-2"><span>🍱</span> Main Packages</div>
                          <div className="grid grid-cols-2 gap-2">
                            {menuData.mainPackages.map(i => (
                              <button key={`qa_main_${i.name}`} onClick={() => amendAdd(amendDraftBundles[amendDraftBundles.length - 1].id, i.name, i.price)}
                                className="px-3 py-2 text-sm bg-white border border-amber-200 rounded-lg hover:bg-amber-50 hover:border-amber-300 button-hover text-left">
                                <div className="font-medium text-gray-800 truncate">{i.name}</div>
                                <div className="text-xs text-amber-700 font-semibold">{formatCedi(i.price)}</div>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                          <div className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2"><span>➕</span> Extras & Add-ons</div>
                          <div className="grid grid-cols-2 gap-2">
                            {menuData.extras.map(i => (
                              <button key={`qa_extra_${i.name}`} onClick={() => amendAdd(amendDraftBundles[amendDraftBundles.length - 1].id, i.name, i.price)}
                                className="px-3 py-2 text-sm bg-white border border-blue-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 button-hover text-left">
                                <div className="font-medium text-gray-800 truncate">{i.name}</div>
                                <div className="text-xs text-blue-700 font-semibold">{formatCedi(i.price)}</div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {amendModalTab === 'details' && (
                <div className="p-6">
                  <div className="max-w-2xl mx-auto space-y-6">
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><span>💰</span> Price Summary</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2">
                          <span className="text-gray-700 font-medium">Original Total</span>
                          <span className="text-xl font-bold text-gray-800">{formatCedi(amendTarget.total)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-t border-blue-200">
                          <span className="text-gray-700 font-medium">New Total</span>
                          <span className="text-xl font-bold text-blue-600">{formatCedi(amendDraftBundles.reduce((t, b) => t + calcItemsTotal(b.items), 0))}</span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-t-2 border-blue-300 bg-white/50 rounded-lg px-3">
                          <span className="text-gray-800 font-bold">Difference</span>
                          <span className={classNames('text-2xl font-bold',
                            (amendDraftBundles.reduce((t, b) => t + calcItemsTotal(b.items), 0) - amendTarget.total) > 0 ? 'text-amber-600' :
                            (amendDraftBundles.reduce((t, b) => t + calcItemsTotal(b.items), 0) - amendTarget.total) < 0 ? 'text-purple-600' : 'text-gray-600'
                          )}>
                            {formatCedi(amendDraftBundles.reduce((t, b) => t + calcItemsTotal(b.items), 0) - amendTarget.total)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                      <label className="text-base font-bold text-gray-800 mb-4 block flex items-center gap-2"><span>💳</span> Payment Method</label>
                      <div className="grid grid-cols-4 gap-3 mb-4">
                        {[{ key: 'momo', label: 'MoMo', emoji: '📱' }, { key: 'cash', label: 'Cash', emoji: '💵' }, { key: 'card', label: 'Card', emoji: '💳' }, { key: 'split', label: 'Split', emoji: '🔀' }].map(m => (
                          <button key={m.key} onClick={() => { setAmendPaymentMethod(m.key); if (m.key !== 'split') setAmendSplitPayments([]); }}
                            className={classNames('px-3 py-3 rounded-xl text-sm font-semibold border-2 transition-all duration-200 button-hover',
                              amendPaymentMethod === m.key ? 'bg-[#B42318] text-white border-[#912018]' : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100')}>
                            <div className="text-2xl mb-1">{m.emoji}</div>{m.label}
                          </button>
                        ))}
                      </div>
                      {amendPaymentMethod === 'split' && (() => {
                        const amendNewTotal = amendDraftBundles.reduce((t, b) => t + calcItemsTotal(b.items), 0);
                        const allocated = amendSplitPayments.reduce((s, p) => s + Number(p.amount), 0);
                        const diff = Number((allocated - amendNewTotal).toFixed(2));
                        return (
                          <div className="border-2 border-blue-200 rounded-xl p-4 bg-blue-50 space-y-4">
                            {amendSplitPayments.length > 0 && (
                              <div className="space-y-2">
                                {amendSplitPayments.map((payment, idx) => (
                                  <div key={idx} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-200">
                                    <span className="text-xl">{payment.method === 'momo' ? '📱' : payment.method === 'cash' ? '💵' : '💳'}</span>
                                    <span className="font-medium text-gray-700 w-16">{methodLabels[payment.method]}</span>
                                    <input type="number" min="0" step="0.01" value={payment.amount}
                                      onChange={e => { const next = [...amendSplitPayments]; next[idx] = { ...next[idx], amount: Number(e.target.value) || 0 }; setAmendSplitPayments(next); }}
                                      className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                    <button onClick={() => setAmendSplitPayments(amendSplitPayments.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg p-1.5 transition-colors">🗑️</button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div>
                              <p className="text-xs font-semibold text-gray-600 mb-2">Add method:</p>
                              <div className="flex gap-2 flex-wrap">
                                {['momo', 'cash', 'card'].map(method => {
                                  const isUsed = amendSplitPayments.some(p => p.method === method);
                                  return (
                                    <button key={method} disabled={isUsed}
                                      onClick={() => { const remaining = amendNewTotal - amendSplitPayments.reduce((s, p) => s + Number(p.amount), 0); setAmendSplitPayments([...amendSplitPayments, { method, amount: remaining > 0 ? Number(remaining.toFixed(2)) : 0 }]); }}
                                      className={classNames('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all',
                                        isUsed ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white border-gray-300 hover:border-blue-400 hover:bg-blue-50 cursor-pointer')}>
                                      <span>{method === 'momo' ? '📱' : method === 'cash' ? '💵' : '💳'}</span>
                                      {methodLabels[method]}
                                      {isUsed && <span className="text-[10px] bg-gray-200 px-1.5 rounded">Added</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            {amendSplitPayments.length > 0 && (
                              <div className="flex justify-between items-center pt-2 border-t border-blue-200 text-sm">
                                <span className="text-gray-600">Allocated / Order Total:</span>
                                <span className={classNames('font-bold', Math.abs(diff) < 0.01 ? 'text-green-600' : 'text-red-600')}>
                                  {formatCedi(allocated)} / {formatCedi(amendNewTotal)}
                                  {Math.abs(diff) >= 0.01 && <span className="ml-2 text-xs">({diff > 0 ? '+' : ''}{formatCedi(diff)})</span>}
                                </span>
                              </div>
                            )}
                            {amendSplitPayments.length < 2 && <p className="text-xs text-amber-600 font-medium">⚠️ Add at least 2 payment methods</p>}
                          </div>
                        );
                      })()}
                      <p className="text-xs text-gray-500 mt-3 bg-gray-50 rounded-lg p-2">
                        <strong>Current:</strong> {methodLabels[amendTarget.paymentMethod] || amendTarget.paymentMethod || 'Unknown'}
                        {amendTarget.paymentMethod === 'split' && amendTarget.splitPayments && (
                          <span className="ml-1">({amendTarget.splitPayments.map(p => `${methodLabels[p.method]} ${formatCedi(p.amount)}`).join(' + ')})</span>
                        )}
                      </p>
                    </div>
                    <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                      <label className="text-base font-bold text-gray-800 mb-4 block flex items-center gap-2"><span>👤</span> Customer Information</label>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name</label>
                          <input type="text" value={amendCustomerName} onChange={(e) => setAmendCustomerName(sanitizeText(e.target.value, 100))}
                            className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Enter customer name" maxLength={100} />
                          <p className="text-xs text-gray-500 mt-1.5 bg-gray-50 rounded px-2 py-1"><strong>Current:</strong> {amendTarget.customerName || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                          <input type="text" value={amendCustomerPhone} onChange={(e) => setAmendCustomerPhone(sanitizeText(e.target.value, 20))}
                            className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Enter phone number" maxLength={20} />
                          <p className="text-xs text-gray-500 mt-1.5 bg-gray-50 rounded px-2 py-1"><strong>Current:</strong> {amendTarget.customerPhone || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Address</label>
                          <textarea value={amendCustomerAddress} onChange={(e) => setAmendCustomerAddress(sanitizeText(e.target.value, 300))} rows="3"
                            className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none" placeholder="Enter delivery address" maxLength={300} />
                          <p className="text-xs text-gray-500 mt-1.5 bg-gray-50 rounded px-2 py-1"><strong>Current:</strong> {amendTarget.deliveryAddress || 'Not set'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-3">
              <div>
                <label htmlFor="amend-reason" className="block text-sm font-semibold text-gray-800 mb-1">
                  Reason for amendment <span className="text-red-600">*</span>
                </label>
                <textarea id="amend-reason" value={amendReason} onChange={(e) => setAmendReason(sanitizeText(e.target.value, 500))} rows="2"
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                  placeholder="What changed and why, e.g. customer swapped Coke for Sobolo" maxLength={500} required />
                <p className="text-xs text-gray-500 mt-1">Saved to the order's amendment history with your name and the time.</p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="text-xs text-gray-600 space-y-1">
                  {(amendTarget.status === 'paid' || amendTarget.status === 'ready') && (
                    <div className="flex items-start gap-2"><span>ℹ️</span><span>A higher total sends the order back to the queue to collect the difference; a lower total records a refund.</span></div>
                  )}
                </div>
                <div className="flex gap-3 w-full sm:w-auto">
                  <button onClick={closeAmend} className="flex-1 sm:flex-none px-6 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 button-hover font-semibold">Cancel</button>
                  <button onClick={confirmAmend} disabled={!amendReason.trim()} className="flex-1 sm:flex-none px-6 py-2.5 bg-[#B42318] text-white rounded-lg hover:bg-[#912018] button-hover font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                    💾 Save Amendment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expense Edit Modal */}
      {showExpenseEditModal && editExpenseTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl card">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Edit Expense</h3>
              <button onClick={closeEditExpense} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="edit-expense-category">Category</label>
                <ExpenseCategorySelect
                  id="edit-expense-category"
                  value={expenseDraft.category}
                  onChange={(category) => setExpenseDraft({ ...expenseDraft, category })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md input-focus"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Amount</label>
                <input type="number" min="0.01" step="0.01" value={expenseDraft.amount} onChange={(e) => setExpenseDraft({...expenseDraft, amount: sanitizeNumeric(e.target.value, { min: 0, max: 99999 })})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md input-focus" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea value={expenseDraft.description} onChange={(e) => setExpenseDraft({...expenseDraft, description: sanitizeText(e.target.value, 500)})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md input-focus" rows="3" maxLength={500}></textarea>
              </div>
              <div className="flex justify-end gap-4">
                <button onClick={closeEditExpense} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 button-hover">Cancel</button>
                <button onClick={confirmEditExpense} className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 button-hover">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expense Approve / Reject Confirm Dialog */}
      <ConfirmDialog
        isOpen={Boolean(confirmDialog)}
        tone={confirmDialog?.type === 'approve' ? 'approve' : 'danger'}
        title={confirmDialog?.type === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
        description={confirmDialog?.message}
        confirmLabel={confirmDialog?.type === 'approve' ? '✅ Approve' : '❌ Reject'}
        onConfirm={confirmExpenseAction}
        onClose={() => setConfirmDialog(null)}
      />

      {/* Expense Delete Confirm */}
      <ConfirmDialog
        isOpen={Boolean(showExpenseDeleteConfirm)}
        tone="danger"
        title="Confirm Delete"
        description="Are you sure you want to delete this expense? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={confirmDeleteExpense}
        onClose={() => { setShowExpenseDeleteConfirm(false); setDeleteExpenseId(null); }}
      />

      {/* Export Data Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg card">
            <div className="bg-emerald-700 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">📤 Export Data</h2>
                <p className="text-green-100 text-sm">Download as CSV for Excel / Google Sheets</p>
              </div>
              <button onClick={() => setShowExportModal(false)} className="text-white/70 hover:text-white text-2xl leading-none">✕</button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">What to export</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[{ key: 'orders', label: '📦 Orders', color: 'blue' }, { key: 'expenses', label: '💸 Expenses', color: 'rose' }].map(({ key, label, color }) => (
                    <button key={key} onClick={() => setExportTypes(prev => ({ ...prev, [key]: !prev[key] }))}
                      className={classNames('flex items-center justify-center gap-2 p-3 rounded-xl border-2 font-semibold text-sm transition-all duration-200',
                        exportTypes[key]
                          ? color === 'blue' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-rose-500 bg-rose-50 text-rose-700'
                          : 'border-gray-200 bg-gray-50 text-gray-400')}>
                      {label}
                      {exportTypes[key] && <span className="text-xs bg-current rounded-full w-4 h-4 flex items-center justify-center text-white" style={{backgroundColor:'currentColor'}}>✓</span>}
                    </button>
                  ))}
                </div>
                {!exportTypes.orders && !exportTypes.expenses && <p className="text-xs text-red-500 mt-1">Select at least one export type</p>}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Date selection</h3>
                <div className="flex gap-2 mb-3">
                  {[{ key: 'single', label: 'Single Day' }, { key: 'range', label: 'Date Range' }].map(({ key, label }) => (
                    <button key={key} onClick={() => setExportRangeType(key)}
                      className={classNames('flex-1 py-2 rounded-lg text-sm font-medium border-2 transition-all',
                        exportRangeType === key ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>
                      {label}
                    </button>
                  ))}
                </div>
                {exportRangeType === 'single' ? (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Select Date</label>
                    <input type="date" value={exportSingleDate} onChange={e => setExportSingleDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                      <input type="date" value={exportStartDate} onChange={e => setExportStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                      <input type="date" value={exportEndDate} min={exportStartDate} onChange={e => setExportEndDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                )}
              </div>
              {exportTypes.orders && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Order statuses to include</h3>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setExportOrderStatuses({ all: true, picked_up: false, delivered: false, paid: false, ready: false, pending_payment: false })}
                      className={classNames('px-3 py-1 text-xs rounded-full border font-medium transition-all', exportOrderStatuses.all ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50')}>
                      All Statuses
                    </button>
                    {[{ key: 'picked_up', label: 'Picked Up' }, { key: 'delivered', label: 'Delivered' }, { key: 'paid', label: 'In Kitchen' }, { key: 'ready', label: 'Ready' }, { key: 'pending_payment', label: 'Pending Payment' }].map(({ key, label }) => (
                      <button key={key} onClick={() => setExportOrderStatuses(prev => {
                        const next = { ...prev, all: false, [key]: !prev[key] };
                        const anyOn = Object.entries(next).filter(([k, v]) => k !== 'all' && v).length > 0;
                        if (!anyOn) return { ...next, all: true };
                        return next;
                      })}
                        className={classNames('px-3 py-1 text-xs rounded-full border font-medium transition-all',
                          !exportOrderStatuses.all && exportOrderStatuses[key] ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50')}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {(() => {
                const start = exportRangeType === 'single' ? new Date(exportSingleDate + 'T00:00:00') : new Date(exportStartDate + 'T00:00:00');
                const end = exportRangeType === 'single' ? new Date(exportSingleDate + 'T23:59:59.999') : new Date(exportEndDate + 'T23:59:59.999');
                let orderCount = orders.filter(o => { const ts = new Date(o.timestamp); return ts >= start && ts <= end; }).length;
                if (!exportOrderStatuses.all) {
                  const active = Object.entries(exportOrderStatuses).filter(([k, v]) => k !== 'all' && v).map(([k]) => k);
                  orderCount = orders.filter(o => { const ts = new Date(o.timestamp); return ts >= start && ts <= end && active.includes(o.status); }).length;
                }
                const expenseCount = expenses.filter(e => { const ts = new Date(e.timestamp); return ts >= start && ts <= end; }).length;
                return (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <p className="text-xs text-gray-500 font-medium mb-2 uppercase tracking-wide">Preview</p>
                    <div className="flex gap-4">
                      {exportTypes.orders && <div className="text-sm"><span className="font-bold text-blue-700 text-lg">{orderCount}</span> <span className="text-gray-600">orders</span></div>}
                      {exportTypes.expenses && <div className="text-sm"><span className="font-bold text-rose-700 text-lg">{expenseCount}</span> <span className="text-gray-600">expenses</span></div>}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {exportRangeType === 'single'
                        ? new Date(exportSingleDate + 'T12:00:00').toLocaleDateString('en-GH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                        : `${new Date(exportStartDate + 'T12:00:00').toLocaleDateString()} – ${new Date(exportEndDate + 'T12:00:00').toLocaleDateString()}`}
                    </p>
                  </div>
                );
              })()}
              <div className="flex justify-end gap-3 pt-1">
                <button onClick={() => setShowExportModal(false)} className="px-5 py-2.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 button-hover font-medium">Cancel</button>
                <button onClick={runExport} disabled={!exportTypes.orders && !exportTypes.expenses}
                  className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 button-hover font-semibold shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
                  ⬇️ Download CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Split Payment Modal */}
      {showSplitPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl card max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">💰 Split Payment</h3>
              <button onClick={() => setShowSplitPaymentModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
            </div>
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm text-gray-600 mb-1">Order Total</div>
              <div className="text-2xl font-bold text-blue-600">{formatCedi(grandTotal)}</div>
            </div>
            <div className="space-y-3 mb-4">
              {splitPayments.map((payment, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{payment.method === 'momo' ? '📱' : payment.method === 'cash' ? '💵' : '💳'}</span>
                      <span className="font-medium text-gray-800">{methodLabels[payment.method]}</span>
                    </div>
                    <div className="text-lg font-semibold text-gray-700">{formatCedi(payment.amount)}</div>
                  </div>
                  <button onClick={() => setSplitPayments(splitPayments.filter((_, i) => i !== index))} className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg p-2 transition-colors">🗑️</button>
                </div>
              ))}
            </div>
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-800 mb-3">Add Payment Method</h4>
              <div className="space-y-3">
                {['momo', 'cash', 'card'].map(method => {
                  const isUsed = splitPayments.some(p => p.method === method);
                  return (
                    <button key={method} disabled={isUsed}
                      onClick={() => { const remaining = grandTotal - splitPayments.reduce((sum, p) => sum + Number(p.amount), 0); const amount = remaining > 0 ? remaining : 0; setSplitPayments([...splitPayments, { method, amount }]); }}
                      className={classNames('w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all duration-200',
                        isUsed ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed' : 'bg-white border-gray-300 hover:border-blue-500 hover:bg-blue-50 cursor-pointer button-hover')}>
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{method === 'momo' ? '📱' : method === 'cash' ? '💵' : '💳'}</span>
                        <span className="font-medium">{methodLabels[method]}</span>
                      </div>
                      {isUsed && <span className="text-xs bg-gray-200 px-2 py-1 rounded">Added</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            {splitPayments.length > 0 && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold text-gray-800 mb-3">Adjust Amounts</h4>
                <div className="space-y-3">
                  {splitPayments.map((payment, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-xl">{payment.method === 'momo' ? '📱' : payment.method === 'cash' ? '💵' : '💳'}</span>
                      <div className="flex-1">
                        <label className="text-xs text-gray-600">{methodLabels[payment.method]}</label>
                        <input type="number" min="0" step="0.01" value={payment.amount}
                          onChange={(e) => { const newPayments = [...splitPayments]; newPayments[index].amount = Number(e.target.value) || 0; setSplitPayments(newPayments); }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {splitPayments.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Total Allocated:</span>
                  <span className="font-semibold text-gray-800">{formatCedi(splitPayments.reduce((sum, p) => sum + Number(p.amount), 0))}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Order Total:</span>
                  <span className="font-semibold text-gray-800">{formatCedi(grandTotal)}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="text-sm font-medium text-gray-700">Difference:</span>
                  <span className={classNames('font-bold',
                    Math.abs(splitPayments.reduce((sum, p) => sum + Number(p.amount), 0) - grandTotal) < 0.01 ? 'text-green-600' : 'text-red-600')}>
                    {formatCedi(splitPayments.reduce((sum, p) => sum + Number(p.amount), 0) - grandTotal)}
                  </span>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setShowSplitPaymentModal(false); setSplitPayments([]); setPosPaymentMethod(''); }}
                className="px-5 py-2.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 button-hover font-medium">Cancel</button>
              <button onClick={() => {
                if (splitPayments.length < 2) return showToast('Add at least 2 payment methods', 'warning');
                const total = splitPayments.reduce((sum, p) => sum + Number(p.amount), 0);
                if (Math.abs(total - grandTotal) > 0.01) return showToast('Split payment amounts must equal the order total', 'warning');
                setPosPaymentMethod('split');
                setShowSplitPaymentModal(false);
                showToast('Split payment configured successfully', 'success');
              }}
                className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 button-hover font-semibold shadow-md">
                Apply Split Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {showRiderAssignmentModal && riderAssignmentTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl card">
            <div className="flex justify-between items-start gap-4 mb-5">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  Assign Rider
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Order #{riderAssignmentTarget.id.slice(-6)} for {riderAssignmentTarget.customerName || 'Online Customer'}
                </p>
              </div>
              <button onClick={closeRiderAssignmentModal} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Order total</div>
                <div className="text-lg font-bold text-gray-800 mt-1">{formatCedi(riderAssignmentTarget.total)}</div>
              </div>
              <div className="rounded-xl bg-sky-50 border border-sky-200 p-3">
                <div className="text-xs text-sky-600 uppercase tracking-wide">Delivery fee</div>
                <div className="text-sm font-medium text-sky-900 mt-1">{formatCedi(getOrderDeliveryFee(riderAssignmentTarget))}</div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registered riders</label>
                <p className="mb-2 text-xs text-gray-500">Riders who are online or already delivering can receive assigned orders. Offline and paused riders stay blocked.</p>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {(riders || []).length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-sm text-gray-500">
                      No active riders found. Ask riders to sign in first.
                    </div>
                  ) : (riders || []).map((rider) => {
                    const presence = riderPresenceById.get(rider.id);
                    const status = presence?.availability_status || 'offline';
                    const selected = riderAssignmentForm.riderId === rider.id;
                    const isAssignable = ['online', 'delivering'].includes(status);
                    return (
                      <button
                        key={rider.id}
                        type="button"
                        disabled={!isAssignable}
                        onClick={() => setRiderAssignmentForm((prev) => ({ ...prev, riderId: rider.id }))}
                        className={classNames(
                          'w-full rounded-xl border px-3 py-3 text-left transition-colors',
                          selected && isAssignable
                            ? 'border-[#B42318] bg-[#fef3f2]'
                            : isAssignable
                              ? 'border-gray-200 bg-white hover:border-[#e4b3ad] hover:bg-[#fdf5f4]'
                              : 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 opacity-70',
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-semibold text-gray-800">{rider.name}</div>
                            <div className="text-xs text-gray-500 mt-1">{rider.phone || 'No phone saved'}</div>
                            {status === 'delivering' && isAssignable && (
                              <div className="mt-2 text-[11px] font-medium text-amber-700">
                                Currently on a delivery, but can still receive another assigned order.
                              </div>
                            )}
                            {!isAssignable && (
                              <div className="mt-2 text-[11px] font-medium text-gray-500">
                                {status === 'paused'
                                  ? 'Paused riders cannot receive new orders.'
                                  : 'Offline riders cannot receive new orders.'}
                              </div>
                            )}
                          </div>
                          <span className={classNames(
                            'rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                            status === 'online' ? 'bg-emerald-100 text-emerald-700' :
                              status === 'delivering' ? 'bg-amber-100 text-amber-700' :
                                status === 'paused' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-600',
                          )}>
                            {String(status).replace(/_/g, ' ')}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  value={riderAssignmentForm.note}
                  onChange={(e) => setRiderAssignmentForm((prev) => ({ ...prev, note: sanitizeText(e.target.value, 250) }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                  placeholder="Optional handoff or dispatch note"
                  maxLength={250}
                />
              </div>

            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={closeRiderAssignmentModal} className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors">
                Cancel
              </button>
              <button
                onClick={confirmRiderAssignment}
                disabled={!riderAssignmentForm.riderId || !canAssignSelectedRider}
                className="px-4 py-2 bg-[#B42318] text-white hover:bg-[#912018] rounded-lg text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:bg-[#d99b95]"
              >
                Assign rider
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rider Assignment Modal */}
      {false && showRiderAssignmentModal && riderAssignmentTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl card">
            <div className="flex justify-between items-start gap-4 mb-5">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  {riderAssignmentForm.markReady ? 'Assign Rider & Mark Ready' : 'Assign Rider'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Order #{riderAssignmentTarget.id.slice(-6)} for {riderAssignmentTarget.customerName || 'Online Customer'}
                </p>
              </div>
              <button onClick={closeRiderDeliveryModal} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Order total</div>
                <div className="text-lg font-bold text-gray-800 mt-1">{formatCedi(riderDeliveryTarget.total)}</div>
              </div>
              <div className="rounded-xl bg-sky-50 border border-sky-200 p-3">
                <div className="text-xs text-sky-600 uppercase tracking-wide">Delivery address</div>
                <div className="text-sm font-medium text-sky-900 mt-1">{riderDeliveryTarget.deliveryAddress || 'No address saved'}</div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rider name</label>
                <input
                  type="text"
                  value={riderDeliveryForm.riderName}
                  onChange={(e) => setRiderDeliveryForm((prev) => ({ ...prev, riderName: sanitizeText(e.target.value, 100) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                  placeholder="Enter rider name"
                  maxLength={100}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rider phone</label>
                  <input
                    type="tel"
                    value={riderDeliveryForm.riderPhone}
                    onChange={(e) => setRiderDeliveryForm((prev) => ({ ...prev, riderPhone: sanitizeText(e.target.value, 20) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                    placeholder="Optional phone"
                    maxLength={20}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payout amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={riderDeliveryForm.payout}
                    onChange={(e) => setRiderDeliveryForm((prev) => ({ ...prev, payout: sanitizeNumeric(e.target.value, { min: 0, max: 99999, fallback: '' }) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  value={riderDeliveryForm.note}
                  onChange={(e) => setRiderDeliveryForm((prev) => ({ ...prev, note: sanitizeText(e.target.value, 250) }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                  placeholder="Optional delivery or payout note"
                  maxLength={250}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={closeRiderDeliveryModal} className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors">
                Cancel
              </button>
              <button onClick={confirmRiderDelivery} className="px-4 py-2 bg-[#B42318] text-white hover:bg-[#912018] rounded-lg text-sm font-semibold transition-colors">
                {riderDeliveryTarget.status === 'delivered' ? 'Save rider payout' : 'Mark delivered'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Placement Confirmation */}
      {showOrderConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl card">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Confirm Order Placement</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to place this order in the queue? Total: {formatCedi(grandTotal)}</p>
            <div className="flex justify-end gap-4">
              <button onClick={() => setShowOrderConfirm(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 button-hover">Cancel</button>
              <button onClick={confirmPlaceOrder} className="px-4 py-2 bg-[#B42318] text-white rounded-md hover:bg-[#912018] button-hover">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* End-of-Day Report Modal */}
      {showEodReport && (() => {
        const r = computeEodReport(eodReportDate);
        const salesOnly = !canViewFinancialReports(currentUser);
        return (
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
              <div className="sticky top-0 bg-indigo-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">📋 End-of-Day Report</h2>
                  <p className="text-indigo-200 text-sm">Komis Kenkey</p>
                </div>
                <div className="flex items-center gap-3">
                  <input type="date" value={eodReportDate} onChange={e => setEodReportDate(e.target.value)}
                    className="px-3 py-1.5 rounded-lg text-sm text-gray-800 font-medium border-0 focus:outline-none focus:ring-2 focus:ring-white" />
                  <button onClick={() => printEodReport(r, { salesOnly })} className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-semibold text-sm transition-colors">🖨️ Print</button>
                  <button onClick={() => setShowEodReport(false)} className="text-white/70 hover:text-white text-2xl leading-none">✕</button>
                </div>
              </div>
              <div className="p-6">
                <EodReport report={r} salesOnly={salesOnly} />
              </div>
            </div>
          </div>
        );
      })()}

      {/* Toast */}
      {notification && (
        <div
          key={notification.id}
          data-leaving={notification.leaving ? 'true' : undefined}
          role="status"
          aria-live="polite"
          className={classNames(
          'pos-toast fixed bottom-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg whitespace-pre-wrap text-left',
          notification.type === 'success' ? 'bg-green-600 text-white' :
          notification.type === 'error' ? 'bg-red-600 text-white' :
          notification.type === 'warning' ? 'bg-yellow-500 text-white' : 'bg-blue-600 text-white'
        )}>
          <span className="mr-2">
            {notification.type === 'success' ? '✅' : notification.type === 'error' ? '❌' : notification.type === 'warning' ? '⚠️' : 'ℹ️'}
          </span>
          {notification.message}
        </div>
      )}

      {/* Celebrate */}
      {celebrateOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="rounded-2xl px-8 py-6 text-center card">
            <div className="text-lg font-semibold">Order placed</div>
          </div>
        </div>
      )}
    </>
  );
};

export default AppModals;
